import { t } from "../i18n";
import type { ReactNode } from "react";
import {
  ClipboardList,
  History,
  RefreshCw,
  Settings,
  UtensilsCrossed,
  Wifi,
  WifiOff
} from "lucide-react";
import type { ConnectionState } from "../useEasyOrders";

export type Screen = "tables" | "history" | "products" | "settings";

type Props = {
  screen: Screen;
  connection: ConnectionState;
  syncing: boolean;
  username: string;
  updateAvailable: boolean;
  isAdmin: boolean;
  tableDetailOpen: boolean;
  /* Hidden while picking a table to move an order to, so the grid is the only thing on offer
     — the Android app empties its bottom navigation for the same reason. */
  navHidden: boolean;
  orderUsesInnerScroll: boolean;
  onNavigate: (screen: Screen) => void;
  onSync: () => void;
  children: ReactNode;
};

export function AppShell({
  screen,
  connection,
  syncing,
  username,
  updateAvailable,
  isAdmin,
  tableDetailOpen,
  navHidden,
  orderUsesInnerScroll,
  onNavigate,
  onSync,
  children
}: Props) {
  return (
    <div className={["app-shell", navHidden ? "nav-hidden" : ""].filter(Boolean).join(" ")}>
      <header className="top-bar">
        <span className="brand-lockup">
          <img src="/icons/icon-192.png" alt="" />
          <strong>EasyOrders</strong>
        </span>
        <span className={`connection-state ${connection}`}>
          {connection === "online" ? <Wifi /> : <WifiOff />}
          <span>{connection === "online" ? username : t("Εκτός σύνδεσης")}</span>
        </span>
        <button
          type="button"
          className="icon-button"
          onClick={onSync}
          aria-label={t("Συγχρονισμός")}
          title={t("Συγχρονισμός")}
          disabled={syncing}
        >
          <RefreshCw className={syncing ? "spinning" : ""} />
        </button>
      </header>

      <div
        className={[
          "app-content",
          screen === "tables" && !tableDetailOpen ? "tables-content" : "",
          screen === "tables" && tableDetailOpen ? "order-content" : "",
          screen === "tables" && tableDetailOpen && orderUsesInnerScroll
            ? "order-inner-content"
            : ""
        ].filter(Boolean).join(" ")}
      >
        {children}
      </div>

      {navHidden ? null : (
      <nav className="bottom-nav" aria-label={t("Κύρια πλοήγηση")}>
        <button
          className={screen === "tables" ? "active" : ""}
          onClick={() => onNavigate("tables")}
          type="button"
        >
          <ClipboardList />
          <span>{t("Τραπέζια")}</span>
        </button>
        <button
          className={screen === "history" ? "active" : ""}
          onClick={() => onNavigate("history")}
          type="button"
        >
          <History />
          <span>{t("Ιστορικό")}</span>
        </button>
        {isAdmin ? (
          <button
            className={screen === "products" ? "active" : ""}
            onClick={() => onNavigate("products")}
            type="button"
          >
            <UtensilsCrossed />
            <span>{t("Προϊόντα")}</span>
          </button>
        ) : null}
        <button
          className={screen === "settings" ? "active" : ""}
          onClick={() => onNavigate("settings")}
          type="button"
        >
          <span className="nav-icon-wrap">
            <Settings />
            {updateAvailable ? <span className="alert-bubble">!</span> : null}
          </span>
          <span>{t("Ρυθμίσεις")}</span>
        </button>
      </nav>
      )}
    </div>
  );
}
